import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ArrowLeft, FileText, BookMarked, CheckSquare, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = { guide: BookMarked, article: FileText, template: Download, checklist: CheckSquare };

export default function ResourcesPage() {
  const [page, setPage] = useState(null);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cms/resources`),
      axios.get(`${API}/articles`)
    ]).then(([cmsRes, artRes]) => {
      setPage(cmsRes.data.content);
      setArticles(artRes.data.articles);
    }).catch(() => {});
  }, []);

  if (!page) return <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center text-[#475569]">Loading...</div>;

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="resources-page">
      <header className="bg-white border-b border-[#E2DFD8]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="text-lg font-bold text-[#3D3229] tracking-wide uppercase" style={{fontFamily:"'Nunito', 'Quicksand', sans-serif", letterSpacing:'0.12em'}}>dsashun</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-[#475569] hover:text-[#1B3A6B]">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#0F1A2A] mb-4" data-testid="resources-title">{page.title}</h1>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto">{page.intro}</p>
        </div>

        {/* Resource Categories */}
        {(page.categories || []).map((cat, ci) => (
          <div key={ci} className="mb-12">
            <h2 className="font-heading text-xl font-bold text-[#1B3A6B] mb-4">{cat.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(cat.items || []).map((item, ii) => {
                const Icon = TYPE_ICONS[item.type] || FileText;
                return (
                  <div key={ii} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className="w-5 h-5 text-[#B8860B]" strokeWidth={1.5} />
                      <span className="text-xs font-medium text-[#B8860B] uppercase">{item.type}</span>
                    </div>
                    <h3 className="font-medium text-[#0F1A2A] mb-2">{item.title}</h3>
                    <p className="text-sm text-[#475569]">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Knowledge Articles */}
        {articles.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-xl font-bold text-[#1B3A6B] mb-4">Knowledge Base</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {articles.map(a => (
                <Link key={a.id} to={`/articles/${a.id}`} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs font-medium text-[#B8860B] uppercase">{a.category}</span>
                  <h3 className="font-medium text-[#0F1A2A] mt-1">{a.title}</h3>
                  <p className="text-sm text-[#475569] mt-1">{new Date(a.created_at).toLocaleDateString()}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-white rounded-2xl p-8 shadow-sm text-center border border-[#E2DFD8]">
          <h3 className="font-heading text-xl font-bold text-[#0F1A2A] mb-2">Ready to Create Your Own?</h3>
          <p className="text-[#475569] mb-4">Sign up for a free trial and start generating Bible studies today.</p>
          <Link to="/login?register=true" className="inline-block px-6 py-3 bg-[#1B3A6B] text-white rounded-lg font-medium hover:bg-[#254B8A] transition-colors" data-testid="resources-cta">
            Start Free Trial
          </Link>
        </div>
      </div>

      <footer className="bg-[#0F1A2A] text-white/60 py-8 px-6 mt-8">
        <div className="max-w-6xl mx-auto text-center text-sm">
          &copy; {new Date().getFullYear()} dsashun. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
